import { formatDistanceToNow } from "date-fns";
import { Lightbulb, FileText, ShieldCheck, CheckCircle2, RotateCcw, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Subject-type adapter — future reviewable subjects register their icon here.
const SUBJECT_META = {
  master_topic: { icon: Lightbulb },
  content_item: { icon: FileText },
  _default: { icon: ShieldCheck },
};

const ROUND_META = {
  pending: { label: "Under Review", cls: "text-warning", Icon: Clock },
  approved: { label: "Approved", cls: "text-success", Icon: CheckCircle2 },
  returned: { label: "Returned for Changes", cls: "text-warning", Icon: RotateCcw },
};

const MY_DECISION = {
  approved: { label: "You approved", cls: "text-success", Icon: CheckCircle2 },
  returned_for_changes: { label: "You returned for changes", cls: "text-warning", Icon: RotateCcw },
};

const age = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } };

// Calm, scannable inbox row. Whitespace/text clicks open the read-only subject
// View — they NEVER approve/return (only the canonical review surface mutates).
export function ApprovalCard({ item, variant, onOpen }) {
  const meta = SUBJECT_META[item.subject_type] || SUBJECT_META._default;
  const Icon = meta.icon;
  const progressReq = item.required_count > 0;
  const allApproved = progressReq && item.approved_count >= item.required_count;

  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(item))}
      data-testid={`approval-card-${item.approval_id}`}
      className="group flex cursor-pointer items-start gap-3 rounded-xl border border-border/70 bg-card p-4 text-left transition-colors hover:border-accent/40 hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold text-foreground" data-testid="approval-title">{item.subject_title}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-muted-foreground">
          <span className="font-medium text-foreground/70">{item.subject_type_label}</span>
          <span aria-hidden>·</span>
          <span>Version {item.subject_version}</span>
        </p>
        <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
          Submitted{item.submitted_by_name ? ` by ${item.submitted_by_name}` : ""} · {age(item.submitted_at)}
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
          {variant === "needs_my_review" && (
            <>
              {progressReq && (
                <span className={cn(allApproved ? "text-success" : "text-muted-foreground")}>
                  {item.approved_count} of {item.required_count} reviewer{item.required_count === 1 ? "" : "s"} approved
                </span>
              )}
              <span className="inline-flex items-center gap-1 font-medium text-accent">
                <Clock className="h-3.5 w-3.5" /> Needs your review
              </span>
            </>
          )}

          {variant === "submitted_by_me" && (() => {
            const rm = ROUND_META[item.round_status] || ROUND_META.pending; const RI = rm.Icon;
            return (
              <>
                <span className={cn("inline-flex items-center gap-1 font-medium", rm.cls)}><RI className="h-3.5 w-3.5" /> {rm.label}</span>
                {progressReq && item.round_status === "pending" && (
                  <span className="text-muted-foreground">{item.approved_count} of {item.required_count} approved</span>
                )}
              </>
            );
          })()}

          {variant === "recently_reviewed" && (() => {
            const dm = MY_DECISION[item.my_decision] || MY_DECISION.approved; const DI = dm.Icon;
            return (
              <>
                <span className={cn("inline-flex items-center gap-1 font-medium", dm.cls)}><DI className="h-3.5 w-3.5" /> {dm.label}</span>
                {item.my_decided_at && <span className="text-muted-foreground">{age(item.my_decided_at)}</span>}
              </>
            );
          })()}
        </div>
      </div>

      <Button
        size="sm" variant={variant === "needs_my_review" ? "default" : "outline"}
        className="shrink-0 gap-1"
        onClick={(e) => { e.stopPropagation(); onOpen(item); }}
        data-testid={`approval-open-${item.approval_id}`}>
        {variant === "needs_my_review" ? "View & Review" : "View"} <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
